import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CompletedModal from "../global/CompletedModal";
import CustomButton from "../Form/CustomButton";
import { formatAmount } from "../../utils/utils";
import { updateDocument, upsertDocument } from "../../firebase/firebaseCrud";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TEST_PUBLIC_KEY);

function BookingCheckoutInner({ totalPrice, orderIdFromServer, bookId, onBookingSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [view, setView] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!view) return;
    const timer = setTimeout(() => {
      setView(false);
      onBookingSuccess?.();
      navigate("/dashboard");
    }, 5000);
    return () => clearTimeout(timer);
  }, [view, navigate, onBookingSuccess]);

  const handleClose = () => {
    setView(false);
    onBookingSuccess?.();
    navigate("/dashboard");
  };

  const finalizeSuccess = async (paymentIntent) => {
    const txPayload = {
      status: "paid",
      transaction_id: paymentIntent.id,
      purchase_time: new Date().toISOString(),
      amount: (paymentIntent.amount / 100).toFixed(2),
      currency: paymentIntent.currency.toUpperCase(),
    };

    // Find the transactions doc by paymentIntentId field (server may use a different doc ID)
    const snap = await getDocs(
      query(collection(db, "transactions"), where("paymentIntentId", "==", paymentIntent.id))
    );

    const txUpdate = snap.empty
      ? upsertDocument("transactions", orderIdFromServer, txPayload)
      : updateDocument("transactions", snap.docs[0].id, txPayload);

    await Promise.all([
      txUpdate,
      bookId ? updateDocument("booking-list", bookId, { status: "paid" }) : Promise.resolve(),
    ]);

    setView(true);
  };

  const handlePayNow = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/bookings/complete?orderId=${orderIdFromServer}`,
        },
        redirect: "if_required",
      });

      if (error) throw new Error(error.message);

      if (paymentIntent?.status === "succeeded") {
        await finalizeSuccess(paymentIntent);
      } else if (paymentIntent?.status) {
        throw new Error(`Payment not completed: ${paymentIntent.status}`);
      }
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <ExpressCheckoutElement onConfirm={handlePayNow} />
      </div>

      <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
        <PaymentElement />
      </div>

      <CustomButton
        color="secondary"
        className="text-sm"
        onClick={handlePayNow}
        fullWidth
        disabled={!stripe || loading}
      >
        {loading ? "Processing..." : `Pay ${formatAmount(totalPrice)}`}
      </CustomButton>

      {view && (
        <CompletedModal
          modalstate={view}
          handleTerminate={handleClose}
          label="Booking Confirmed!"
          text={`Order ID: ${orderIdFromServer}`}
          statement="Your booking is confirmed. We look forward to seeing you!"
        />
      )}
    </>
  );
}

export default function BookingStripeButton({ amount, selectedDateTime, userId, category, service, onSuccess }) {
  const [clientSecret, setClientSecret] = useState("");
  const [orderId, setOrderId] = useState("");
  const [bookId, setBookId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!amount || !selectedDateTime || !userId || !category || !service) return;

    setClientSecret("");
    setOrderId("");
    setBookId("");
    setError("");

    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          "https://server.rentnow.com.ng/strandz/stripe/payment/booking",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
            body: JSON.stringify({
              userId,
              amount,
              selectedDate: selectedDateTime,
              category,
              service,
            }),
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to create booking payment");

        setClientSecret(data.clientSecret);
        setOrderId(data.orderId);
        setBookId(data.bookId || "");
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error(e);
          setError(e.message);
        }
      }
    })();

    return () => controller.abort();
  }, [amount, selectedDateTime, userId, category, service]);

  const options = useMemo(() => {
    if (!clientSecret) return undefined;
    return { clientSecret, appearance: { theme: "stripe" } };
  }, [clientSecret]);

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
        {error}
      </Typography>
    );
  }

  if (!clientSecret || !options) {
    return (
      <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
        Preparing payment...
      </Typography>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options} key={clientSecret}>
      <BookingCheckoutInner
        totalPrice={amount}
        orderIdFromServer={orderId}
        bookId={bookId}
        onBookingSuccess={onSuccess}
      />
    </Elements>
  );
}
